import Phaser from 'phaser';
import { GAME } from '../constants';
import { VictoryReward, getModifierColor, AugmentRewardOption } from '../cards/ModifierGen';
import { RunData, addAbility, addCardToRun, addRelic } from '../run/RunState';
import { Relic, generateEliteRelicReward, generateBossRelicChoice } from '../relics/Relic';
import { CardModifier, NodeType } from '../types';
import { PIXEL_THEME, FONT_FAMILY, createPixelPanel, drawPixelBackdrop } from '../ui/PixelTheme';
import { fadeIn, fadeOut } from '../ui/SceneTransitions';

function describeEffect(type: string): string {
  switch (type) {
    case 'extra_damage': return 'bonus damage';
    case 'extra_block': return 'bonus block';
    case 'heal': return 'healing';
    case 'draw': return 'draw bonus';
    case 'draw_penalty': return 'draw penalty';
    case 'buff_attack': return 'attack buff';
    case 'debuff_enemy': return 'enemy debuff';
    case 'zone_cap_attack': return 'ATK cap';
    case 'zone_cap_defense': return 'DEF cap';
    case 'zone_cap_ability': return 'ABL cap';
    case 'zone_cap_all': return 'all zone caps';
    default: return type;
  }
}

export class RewardScene extends Phaser.Scene {
  private picked = false;
  private relicPicked = false;
  private requiresRelicChoice = false;
  private augmentOverlay: Phaser.GameObjects.GameObject[] = [];
  private skipBtn!: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'RewardScene' });
  }

  create(data: { rewards: VictoryReward[]; run: RunData; nodeType?: NodeType }): void {
    this.picked = false;
    this.relicPicked = false;
    this.requiresRelicChoice = false;
    this.augmentOverlay = [];

    fadeIn(this);
    const { rewards, run } = data;
    const nodeType = data.nodeType ?? 'combat';
    const cx = GAME.WIDTH / 2;
    run.abilities = run.abilities ?? [];

    drawPixelBackdrop(this);
    const topPanel = createPixelPanel(this, cx, 52, GAME.WIDTH - 80, 86, {
      fill: 0x1d1626,
      stroke: 0xc9663b,
      accent: 0xffd68f,
    });
    topPanel.elements.forEach(el => el.setDepth(1));

    this.add.text(cx, 28, 'VICTORY - CHOOSE A REWARD', {
      fontSize: '16px',
      fontFamily: FONT_FAMILY,
      color: '#ffe7ab',
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(2);

    this.add.text(cx, 58, 'Pick ONE: Number card, Persistent ability, or Augment', {
      fontSize: '8px',
      fontFamily: FONT_FAMILY,
      color: PIXEL_THEME.text,
    }).setOrigin(0.5).setDepth(2);

    this.add.text(cx, 80, `HP: ${run.playerHp}/${run.playerMaxHp}  |  Deck: ${run.deckNumbers.length}  |  Wins: ${run.battlesWon}`, {
      fontSize: '8px',
      fontFamily: FONT_FAMILY,
      color: PIXEL_THEME.textMuted,
    }).setOrigin(0.5).setDepth(2);

    const cardWidth = 212;
    const cardHeight = 252;
    const spacing = 228;
    const startX = cx - (rewards.length - 1) * spacing / 2;
    const cardY = nodeType === 'boss' ? 222 : 236;

    for (let i = 0; i < rewards.length; i++) {
      const reward = rewards[i];
      const x = startX + i * spacing;
      this.createRewardChoice(x, cardY, cardWidth, cardHeight, reward, run);
    }

    const relicY = cardY + cardHeight / 2 + 56;

    if (nodeType === 'elite') {
      const relic = generateEliteRelicReward(run.relics.map(r => r.id));
      if (relic) {
        this.requiresRelicChoice = true;
        this.showRelicReward(cx, relicY, relic, run);
      }
    }

    if (nodeType === 'boss') {
      const bossRelics = generateBossRelicChoice(run.relics.map(r => r.id));
      if (bossRelics.length > 0) {
        this.requiresRelicChoice = true;
        this.showBossRelicChoice(cx, relicY, bossRelics, run);
      }
    }

    this.skipBtn = this.add.text(cx, GAME.HEIGHT - 32, '[ SKIP REWARD  S ]', {
      fontSize: '10px',
      fontFamily: FONT_FAMILY,
      color: '#ffd6a0',
      backgroundColor: '#322638',
      padding: { x: 16, y: 8 },
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setDepth(4);

    this.skipBtn.on('pointerover', () => this.skipBtn.setColor('#fff2d0'));
    this.skipBtn.on('pointerout', () => this.skipBtn.setColor('#ffd6a0'));
    this.skipBtn.on('pointerdown', () => this.handleSkip(run));

    this.add.text(cx, GAME.HEIGHT - 56, this.requiresRelicChoice
      ? 'You can skip cards, but elite/boss relic choice is still required.'
      : 'Tip: press S to skip rewards quickly.', {
      fontSize: '10px',
      fontFamily: FONT_FAMILY,
      color: '#9eb4df',
      align: 'center',
    }).setOrigin(0.5).setDepth(3);

    this.input.keyboard?.on('keydown-S', () => this.handleSkip(run));
    this.input.keyboard?.on('keydown-ESC', () => this.closeAugmentOverlay());
  }

  private createRewardChoice(
    x: number,
    y: number,
    w: number,
    h: number,
    reward: VictoryReward,
    run: RunData,
  ): void {
    const bgColor = reward.kind === 'ability'
      ? 0x1f3527
      : reward.kind === 'augment'
        ? 0x332629
        : 0x21283d;
    const borderColor = reward.kind === 'ability'
      ? 0x7de3a2
      : reward.kind === 'augment'
        ? 0xffb18c
        : 0x7fa8ff;

    this.add.rectangle(x + 4, y + 4, w, h, 0x000000, 0.42).setDepth(2);
    const bg = this.add.rectangle(x, y, w, h, bgColor)
      .setStrokeStyle(2, borderColor, 1)
      .setInteractive({ useHandCursor: true });
    this.add.rectangle(x, y, w - 8, h - 8, 0x111625, 0.36)
      .setStrokeStyle(1, borderColor, 0.48)
      .setDepth(bg.depth + 1);
    this.add.rectangle(x, y - h / 2 + 6, w - 12, 4, borderColor, 0.72).setDepth(bg.depth + 1);

    if (reward.kind === 'number') {
      const numColor = reward.number < 0 ? '#ff6666' : '#ffffff';
      const absValue = Math.abs(reward.number);
      this.add.text(x, y - 96, 'NUMBER CARD', {
        fontSize: '11px',
        fontFamily: FONT_FAMILY,
        color: '#bed2ff',
      }).setOrigin(0.5);

      this.add.text(x, y - 36, `${reward.number}`, {
        fontSize: '32px',
        fontFamily: FONT_FAMILY,
        color: numColor,
        fontStyle: 'bold',
      }).setOrigin(0.5);

      const rulesText = reward.number < 0
        ? `ATK: enemy heals ${absValue}\nDEF: lose ${absValue} block\nABL: no base effect`
        : `ATK: deal ${reward.number} damage\nDEF: gain ${reward.number} block\nABL: no base effect`;

      this.add.text(x, y + 26, rulesText, {
        fontSize: '9px',
        fontFamily: FONT_FAMILY,
        color: '#d9def2',
        align: 'center',
        lineSpacing: 3,
        wordWrap: { width: w - 24 },
      }).setOrigin(0.5);

      this.add.text(x, y + 92, reward.number < 0
        ? 'Risky: can heal enemy or cut your block'
        : 'Stable basic value card', {
        fontSize: '10px',
        fontFamily: FONT_FAMILY,
        color: reward.number < 0 ? '#ff9f8d' : '#9fc4ff',
        align: 'center',
        wordWrap: { width: w - 18 },
      }).setOrigin(0.5);

      bg.on('pointerdown', () => {
        if (this.picked) return;
        this.picked = true;
        addCardToRun(run, reward.number);
        this.showFeedback(x, y - 122, `Card ${reward.number} added`, '#ffd700');
        this.maybeContinue(run);
      });
    } else if (reward.kind === 'ability') {
      const modColor = getModifierColor(reward.ability.modifier.trigger);
      const trigger = reward.ability.modifier.trigger.toUpperCase();
      const mod = reward.ability.modifier;
      const effectValue = mod.effect.value;
      const effectName = describeEffect(mod.effect.type);

      this.add.text(x, y - 96, 'PERSISTENT ABILITY', {
        fontSize: '11px',
        fontFamily: FONT_FAMILY,
        color: '#bfffd4',
      }).setOrigin(0.5);

      this.add.text(x, y - 58, reward.ability.name.toUpperCase(), {
        fontSize: '10px',
        fontFamily: FONT_FAMILY,
        color: modColor,
        fontStyle: 'bold',
      }).setOrigin(0.5);

      this.add.text(x, y - 30, `[${trigger}] +${effectValue} ${effectName}`, {
        fontSize: '9px',
        fontFamily: FONT_FAMILY,
        color: modColor,
        align: 'center',
        wordWrap: { width: w - 18 },
      }).setOrigin(0.5);

      this.add.text(x, y + 18, 'Each turn: 1 random drawn number card\ngets this modifier [TEMP]', {
        fontSize: '9px',
        fontFamily: FONT_FAMILY,
        color: '#d4efdf',
        align: 'center',
        lineSpacing: 2,
        wordWrap: { width: w - 18 },
      }).setOrigin(0.5);

      this.add.text(x, y + 90, `Only active in ${trigger} zone`, {
        fontSize: '10px',
        fontFamily: FONT_FAMILY,
        color: '#9bd2af',
      }).setOrigin(0.5);

      bg.on('pointerdown', () => {
        if (this.picked) return;
        this.picked = true;
        addAbility(run, reward.ability);
        this.showFeedback(x, y - 122, `${reward.ability.name} learned`, '#66cc66');
        this.maybeContinue(run);
      });
    } else {
      this.drawAugmentRewardCard(x, y, w, reward);
      bg.on('pointerdown', () => {
        if (this.picked) return;
        this.showAugmentPicker(run, reward);
      });
    }

    bg.on('pointerover', () => bg.setStrokeStyle(3, 0xffe28f));
    bg.on('pointerout', () => bg.setStrokeStyle(2, borderColor, 1));
  }

  private drawAugmentRewardCard(x: number, y: number, w: number, reward: AugmentRewardOption): void {
    const mod = reward.modifier;
    const trigger = mod.trigger.toUpperCase();
    const rarity = mod.rarity?.toUpperCase() ?? 'COMMON';
    const effect = describeEffect(mod.effect.type);

    this.add.text(x, y - 96, 'AUGMENT', {
      fontSize: '11px',
      fontFamily: FONT_FAMILY,
      color: '#ffd1b6',
    }).setOrigin(0.5);

    this.add.text(x, y - 58, reward.title.toUpperCase(), {
      fontSize: '10px',
      fontFamily: FONT_FAMILY,
      color: '#ffd8c1',
      fontStyle: 'bold',
      align: 'center',
      wordWrap: { width: w - 20 },
    }).setOrigin(0.5);

    this.add.text(x, y - 20, `[${trigger}] +${mod.effect.value} ${effect}`, {
      fontSize: '9px',
      fontFamily: FONT_FAMILY,
      color: '#ffe3cf',
      align: 'center',
      wordWrap: { width: w - 20 },
    }).setOrigin(0.5);

    this.add.text(x, y + 30, `${reward.description}\n\nApply to a deck card of your choice`, {
      fontSize: '9px',
      fontFamily: FONT_FAMILY,
      color: '#e2ccbf',
      align: 'center',
      lineSpacing: 3,
      wordWrap: { width: w - 22 },
    }).setOrigin(0.5);

    this.add.text(x, y + 96, `Rarity: ${rarity}`, {
      fontSize: '10px',
      fontFamily: FONT_FAMILY,
      color: '#ffc89f',
    }).setOrigin(0.5);
  }

  private showAugmentPicker(run: RunData, reward: AugmentRewardOption): void {
    this.closeAugmentOverlay();

    const overlayPanel = createPixelPanel(
      this,
      GAME.WIDTH / 2,
      GAME.HEIGHT / 2,
      GAME.WIDTH - 120,
      GAME.HEIGHT - 120,
      { fill: 0x111528, stroke: 0xcf7343, accent: 0xffd895, alpha: 0.96 },
    );
    overlayPanel.elements.forEach(el => el.setDepth(100));
    const overlay = overlayPanel.bg.setInteractive();

    const title = this.add.text(GAME.WIDTH / 2, 92, `CHOOSE CARD FOR ${reward.title.toUpperCase()}`, {
      fontSize: '12px',
      fontFamily: FONT_FAMILY,
      color: '#ffe1b3',
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(101);

    const subtitle = this.add.text(GAME.WIDTH / 2, 120,
      `Rule: empty slot = apply, same trigger/effect = stack +${reward.modifier.effect.value}, else replace`, {
        fontSize: '11px',
        fontFamily: FONT_FAMILY,
        color: '#b8c5e8',
      }).setOrigin(0.5).setDepth(101);

    this.augmentOverlay.push(overlay, title, subtitle);

    const sorted = [...run.deckNumbers].sort((a, b) => a - b);
    const cols = 5;
    const startX = GAME.WIDTH / 2 - (cols - 1) * 95;
    const startY = 190;

    for (let i = 0; i < sorted.length; i++) {
      const n = sorted[i];
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = startX + col * 190;
      const y = startY + row * 80;
      const existing = run.modifiers.get(n);

      const bg = this.add.rectangle(x, y, 170, 64, 0x2a2735)
        .setStrokeStyle(1, 0xbd7a53)
        .setDepth(101)
        .setInteractive({ useHandCursor: true });

      const valueLabel = this.add.text(x, y - 12, `${n}`, {
        fontSize: '12px',
        fontFamily: FONT_FAMILY,
        color: n < 0 ? '#ff8888' : '#ffffff',
        fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(102);

      const existingLabel = this.add.text(x, y + 12,
        existing
          ? `has: ${existing.trigger.toUpperCase()} ${describeEffect(existing.effect.type)} +${existing.effect.value}`
          : 'has: no modifier', {
            fontSize: '9px',
            fontFamily: FONT_FAMILY,
            color: existing ? '#ffe2a3' : '#a7b4d5',
            align: 'center',
            wordWrap: { width: 160 },
          }).setOrigin(0.5).setDepth(102);

      bg.on('pointerover', () => bg.setStrokeStyle(2, 0xffde8e));
      bg.on('pointerout', () => bg.setStrokeStyle(1, 0xbd7a53));
      bg.on('pointerdown', () => {
        const message = this.applyAugmentToCard(run, n, reward.modifier);
        this.picked = true;
        this.closeAugmentOverlay();
        this.showFeedback(GAME.WIDTH / 2, GAME.HEIGHT - 96, message, '#d6b8ff');
        this.maybeContinue(run);
      });

      this.augmentOverlay.push(bg, valueLabel, existingLabel);
    }

    const cancel = this.add.text(GAME.WIDTH / 2, GAME.HEIGHT - 74, '[ CANCEL ]', {
      fontSize: '10px',
      fontFamily: FONT_FAMILY,
      color: '#ffd9a6',
      backgroundColor: '#342638',
      padding: { x: 12, y: 8 },
    }).setOrigin(0.5).setDepth(101).setInteractive({ useHandCursor: true });

    cancel.on('pointerover', () => cancel.setColor('#fff2d4'));
    cancel.on('pointerout', () => cancel.setColor('#ffd9a6'));
    cancel.on('pointerdown', () => this.closeAugmentOverlay());

    this.augmentOverlay.push(cancel);
  }

  private closeAugmentOverlay(): void {
    if (this.augmentOverlay.length === 0) return;
    for (const el of this.augmentOverlay) el.destroy();
    this.augmentOverlay = [];
  }

  private handleSkip(run: RunData): void {
    if (this.picked) return;
    this.picked = true;
    this.showFeedback(GAME.WIDTH / 2, GAME.HEIGHT - 70, 'Reward skipped', '#777788');
    this.maybeContinue(run);
  }

  private applyAugmentToCard(run: RunData, cardNumber: number, incoming: CardModifier): string {
    const existing = run.modifiers.get(cardNumber);
    const copied: CardModifier = {
      ...incoming,
      effect: { ...incoming.effect },
    };

    if (!existing) {
      run.modifiers.set(cardNumber, copied);
      return `Augment applied to ${cardNumber}`;
    }

    const sameEffectType = existing.trigger === copied.trigger && existing.effect.type === copied.effect.type;
    if (sameEffectType) {
      existing.effect.value += copied.effect.value;
      run.modifiers.set(cardNumber, existing);
      return `Stacked on ${cardNumber}: now +${existing.effect.value}`;
    }

    run.modifiers.set(cardNumber, copied);
    return `Replaced modifier on ${cardNumber}`;
  }

  private showRelicReward(cx: number, y: number, relic: Relic, run: RunData): void {
    this.add.text(cx, y - 10, 'ELITE BONUS: PICK RELIC', {
      fontSize: '9px',
      fontFamily: FONT_FAMILY,
      color: '#9ff6e8',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    const bg = this.add.rectangle(cx, y + 35, 320, 64, 0x1a2f2b)
      .setStrokeStyle(2, 0x5abf9f)
      .setInteractive({ useHandCursor: true });

    this.add.text(cx, y + 22, relic.name.toUpperCase(), {
      fontSize: '10px',
      fontFamily: FONT_FAMILY,
      color: '#c9fff1',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.add.text(cx, y + 44, relic.description, {
      fontSize: '11px',
      fontFamily: FONT_FAMILY,
      color: '#a7ddd2',
      align: 'center',
      wordWrap: { width: 300 },
    }).setOrigin(0.5);

    bg.on('pointerover', () => bg.setStrokeStyle(3, 0xffd68f));
    bg.on('pointerout', () => bg.setStrokeStyle(2, 0x5abf9f));
    bg.on('pointerdown', () => {
      if (this.relicPicked) return;
      this.relicPicked = true;
      addRelic(run, relic);
      this.showFeedback(cx, y - 20, `${relic.name} acquired`, '#8cefd2');
      this.maybeContinue(run);
    });
  }

  private showBossRelicChoice(cx: number, y: number, relics: Relic[], run: RunData): void {
    this.add.text(cx, y - 12, 'BOSS BONUS: PICK RELIC', {
      fontSize: '9px',
      fontFamily: FONT_FAMILY,
      color: '#ff8800',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    const spacing = 220;
    const startX = cx - (relics.length - 1) * spacing / 2;

    for (let i = 0; i < relics.length; i++) {
      const relic = relics[i];
      const x = startX + i * spacing;

      const bg = this.add.rectangle(x, y + 40, 190, 74, 0x442200)
        .setStrokeStyle(2, 0xff8800)
        .setInteractive({ useHandCursor: true });

      this.add.text(x, y + 28, relic.name.toUpperCase(), {
        fontSize: '9px',
        fontFamily: FONT_FAMILY,
        color: '#ffcc66',
        fontStyle: 'bold',
      }).setOrigin(0.5);

      this.add.text(x, y + 50, relic.description, {
        fontSize: '10px',
        fontFamily: FONT_FAMILY,
        color: '#ccaa66',
        align: 'center',
        wordWrap: { width: 170 },
      }).setOrigin(0.5);

      bg.on('pointerover', () => bg.setStrokeStyle(3, 0xffd700));
      bg.on('pointerout', () => bg.setStrokeStyle(2, 0xff8800));
      bg.on('pointerdown', () => {
        if (this.relicPicked) return;
        this.relicPicked = true;
        addRelic(run, relic);
        this.showFeedback(x, y - 4, `${relic.name} acquired`, '#ff8800');
        this.maybeContinue(run);
      });
    }
  }

  private maybeContinue(run: RunData): void {
    if (!this.picked) return;
    if (this.requiresRelicChoice && !this.relicPicked) {
      this.showFeedback(GAME.WIDTH / 2, GAME.HEIGHT - 88, 'Pick your relic to continue', '#ffcc66');
      return;
    }
    this.time.delayedCall(700, () => this.nextBattle(run));
  }

  private showFeedback(x: number, y: number, text: string, color: string): void {
    const t = this.add.text(x, y, text, {
      fontSize: '11px',
      fontFamily: FONT_FAMILY,
      color,
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.tweens.add({
      targets: t,
      y: y - 26,
      alpha: 0,
      duration: 850,
      ease: 'Power2',
    });
  }

  private nextBattle(run: RunData): void {
    fadeOut(this).then(() => this.scene.start('MapScene', { run }));
  }
}
